// Session Management Utility for E8 Tracker
// Handles session timeouts, token refresh, and cache invalidation

import { supabase } from './supabase'
import authAPI from './authAPI'
import toast from 'react-hot-toast'
import { clearDirectBrowserAuthority, failClosedBrowserAuthority, terminateBrowserAuthority } from '../services/browserState'

class SessionManager {
  constructor() {
    this.INACTIVITY_TIMEOUT = 15 * 60 * 1000 // 15 minutes in milliseconds
    this.TOKEN_REFRESH_BUFFER = 5 * 60 * 1000 // Refresh 5 minutes before expiry
    this.ACTIVITY_CHECK_INTERVAL = 60 * 1000 // Check every minute
    
    this.lastActivity = Date.now()
    this.sessionCheckInterval = null
    this.tokenRefreshTimeout = null
    this.isSessionActive = false
    this.activityTrackingActive = false
    this.monitoringActive = false
    this.sessionSource = null
    this.customSession = null
    
    // Bind methods to preserve context
    this.trackActivity = this.trackActivity.bind(this)
    this.checkSession = this.checkSession.bind(this)
    this.refreshTokens = this.refreshTokens.bind(this)
    this.visibilityHandler = () => {
      if (!document.hidden) {
        this.trackActivity()
        this.checkSession()
      }
    }
    
    this.start()
    
    console.log('🕐 SessionManager initialized with 15-minute inactivity timeout')
  }

  /**
   * Setup activity tracking listeners
   */
  setupActivityTracking() {
    if (this.activityTrackingActive) return
    // Track user activity events
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    
    activityEvents.forEach(event => {
      document.addEventListener(event, this.trackActivity, true)
    })
    
    // Track page visibility changes
    document.addEventListener('visibilitychange', this.visibilityHandler)
    this.activityTrackingActive = true
    
    console.log('🕐 Activity tracking enabled for session management')
  }

  /**
   * Track user activity and reset inactivity timer
   */
  trackActivity() {
    this.lastActivity = Date.now()
    
    // If session was inactive, check if we need to refresh
    if (!this.isSessionActive) {
      this.checkSession()
    }
  }

  /**
   * Setup periodic session monitoring
   */
  setupSessionMonitoring() {
    if (this.monitoringActive) return
    // Check session status every minute
    this.sessionCheckInterval = setInterval(this.checkSession, this.ACTIVITY_CHECK_INTERVAL)
    this.monitoringActive = true
  }

  start() {
    this.setupActivityTracking()
    this.setupSessionMonitoring()
  }

  activateSession(session, { source = 'direct' } = {}) {
    this.start()
    this.sessionSource = source
    this.customSession = source === 'custom' ? session : null
    this.isSessionActive = true
    this.lastActivity = Date.now()
    this.scheduleTokenRefresh(session)
  }

  /**
   * Check current session status and handle timeouts
   */
  async checkSession() {
    try {
      const { data: { session: directSession }, error } = await supabase.auth.getSession()
      
      if (error) {
        console.error('🕐 Session check failed:', error)
        await this.signOut()
        return false
      }

      const session = directSession || (authAPI.token ? {
        ...this.customSession,
        access_token: authAPI.token,
        refresh_token: authAPI.refreshToken,
        user: this.customSession?.user || (authAPI.userId ? { id: authAPI.userId } : null),
      } : null)
      this.sessionSource = directSession ? 'direct' : session ? 'custom' : null

      if (!session) {
        console.log('🕐 No active session found')
        this.isSessionActive = false
        return
      }

      const now = Date.now()
      const timeSinceActivity = now - this.lastActivity
      const tokenExpiry = new Date(session.expires_at * 1000).getTime()
      const timeUntilExpiry = tokenExpiry - now

      // Check for inactivity timeout
      if (timeSinceActivity > this.INACTIVITY_TIMEOUT) {
        console.log('🕐 Session timeout due to inactivity - signing out')
        toast.error('Session expired due to inactivity. Please sign in again.')
        await this.signOut()
        return
      }

      // Check if token needs refresh
      if (timeUntilExpiry < this.TOKEN_REFRESH_BUFFER) {
        console.log('🕐 Token approaching expiry - refreshing...')
        await this.refreshTokens()
      }

      this.isSessionActive = true
      
      // Ensure tokens are synced to authAPI (handles page refresh case)
      if (this.sessionSource === 'direct') await this.ensureTokenSync(session)
      
    } catch (error) {
      console.error('🕐 Session check error:', error)
      await this.signOut()
      return false
    }
  }

  /**
   * Ensure tokens are properly synced to authAPI
   */
  async ensureTokenSync(session) {
    if (!session?.access_token) return

    const directUserId = session.user?.id
    if (authAPI.userId && directUserId && authAPI.userId !== directUserId) {
      console.warn('🕐 Direct session identity mismatch - removing stale direct authority')
      await clearDirectBrowserAuthority({ directClient: supabase, failClosed: failClosedBrowserAuthority })
      return false
    }

    // Check if authAPI has tokens
    if (!authAPI.token || authAPI.token !== session.access_token) {
      console.log('🕐 Syncing session tokens to authAPI (page refresh recovery)')
      authAPI.saveTokensToStorage(session.access_token, session.refresh_token, directUserId, 'direct')
      
      // Verify sync worked
      if (authAPI.token === session.access_token) {
        console.log('🕐 ✅ Token sync successful')
        return true
      } else {
        console.error('🕐 ❌ Token sync failed')
        return false
      }
    }
    
    return true
  }

  /**
   * Refresh session tokens
   */
  async refreshTokens() {
    try {
      console.log('🕐 Refreshing session tokens...')
      
      const refreshResult = this.sessionSource === 'custom'
        ? await authAPI.refreshSession()
        : await supabase.auth.refreshSession()
      const { data, error } = refreshResult
      const session = data?.session
      
      if (error) {
        console.error('🕐 Token refresh failed:', error)
        toast.error('Session refresh failed. Please sign in again.')
        await this.signOut()
        return false
      }

      if (session) {
        if (this.sessionSource === 'direct') {
          authAPI.saveTokensToStorage(session.access_token, session.refresh_token, session.user?.id, 'direct')
        } else {
          this.customSession = { ...session, user: data?.user || this.customSession?.user }
        }
        console.log('🕐 ✅ Tokens refreshed successfully')
        
        // Schedule next refresh
        this.scheduleTokenRefresh(session)
        return true
      }

      console.error('🕐 Token refresh returned no verifiable session')
      await this.signOut()
      return false
    } catch (error) {
      console.error('🕐 Token refresh error:', error)
      await this.signOut()
      return false
    }
  }

  /**
   * Schedule next token refresh
   */
  scheduleTokenRefresh(session) {
    if (this.tokenRefreshTimeout) {
      clearTimeout(this.tokenRefreshTimeout)
    }

    const tokenExpiry = new Date(session.expires_at * 1000).getTime()
    const refreshTime = tokenExpiry - Date.now() - this.TOKEN_REFRESH_BUFFER
    
    if (refreshTime > 0) {
      this.tokenRefreshTimeout = setTimeout(this.refreshTokens, refreshTime)
      console.log(`🕐 Next token refresh scheduled in ${Math.round(refreshTime / 60000)} minutes`)
    }
  }

  /**
   * Handle session initialization (on app start or page refresh)
   */
  async initializeSession() {
    try {
      console.log('🕐 Initializing session management...')
      this.start()
      
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (error) {
        console.error('🕐 Session initialization failed:', error)
        return false
      }

      if (session) {
        console.log('🕐 Active session found - ensuring token sync')
        
        // Ensure tokens are synced to authAPI
        const syncSuccess = await this.ensureTokenSync(session)
        
        if (syncSuccess) {
          this.activateSession(session, { source: 'direct' })
          console.log('🕐 ✅ Session initialization complete')
          return true
        } else {
          console.error('🕐 ❌ Session initialization failed - token sync error')
          return false
        }
      } else if (authAPI.token && authAPI.userId) {
        const customSession = this.customSession || {
          access_token: authAPI.token,
          refresh_token: authAPI.refreshToken,
          user: { id: authAPI.userId },
        }
        this.activateSession(customSession, { source: 'custom' })
        console.log('🕐 ✅ Custom session initialization complete')
        return true
      } else {
        console.log('🕐 No active session found')
        this.isSessionActive = false
        return false
      }
      
    } catch (error) {
      console.error('🕐 Session initialization error:', error)
      return false
    }
  }

  /**
   * Sign out and cleanup
   */
  async signOut() {
    try {
      await terminateBrowserAuthority({
        authClient: authAPI,
        directClient: supabase,
        manager: this,
        failClosed: failClosedBrowserAuthority,
      })
      authAPI.clearTokens()
      console.log('🕐 Session manager cleanup complete')
    } catch (error) {
      authAPI.clearTokens()
      console.error('🕐 Sign out error:', error)
    }
  }

  /**
   * Cleanup event listeners
   */
  cleanup() {
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    if (this.activityTrackingActive) {
      activityEvents.forEach(event => {
        document.removeEventListener(event, this.trackActivity, true)
      })
      document.removeEventListener('visibilitychange', this.visibilityHandler)
    }
    
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval)
    }
    if (this.tokenRefreshTimeout) {
      clearTimeout(this.tokenRefreshTimeout)
    }
    this.sessionCheckInterval = null
    this.tokenRefreshTimeout = null
    this.isSessionActive = false
    this.activityTrackingActive = false
    this.monitoringActive = false
    this.sessionSource = null
    this.customSession = null
    
    console.log('🕐 SessionManager cleanup complete')
  }

  /**
   * Get session status info
   */
  getSessionInfo() {
    const timeSinceActivity = Date.now() - this.lastActivity
    const minutesInactive = Math.floor(timeSinceActivity / 60000)
    const timeoutMinutes = Math.floor(this.INACTIVITY_TIMEOUT / 60000)
    
    return {
      isActive: this.isSessionActive,
      minutesInactive,
      timeoutMinutes,
      timeUntilTimeout: Math.max(0, timeoutMinutes - minutesInactive)
    }
  }
}

// Create singleton instance
const sessionManager = new SessionManager()

export default sessionManager
